import type {AcademyParticipationRowDTO} from "crqlar-react-common/dist/models/aggregation/academyStatistics.model";

/**
 * The Akademie participation export: one line per booking, for HR's spreadsheet.
 *
 * **Flat, not summarised.** HR administers the Akademie in Excel for another year, so what they
 * need is the raw list they can pivot themselves: who sat in which course, what it counted
 * towards, and what it paid. Any summary they might want can be built from this, and picking one
 * here would be picking for them.
 *
 * **Every booking is a line, whatever its status.** A declined request and a booked seat nobody
 * turned up to are facts HR has to see. Attendance is a column, not a filter.
 *
 * **Credits stay in one column that can simply be summed.** The API writes 0 on everything that
 * was never attended, so a total over this column is what the Akademie actually paid out in the
 * range. The course's catalogue value sits in its own column, because the two differ once the
 * catalogue is edited and a spreadsheet should show that rather than hide it.
 *
 * Kept pure and separate from the download so the file's shape is settled by tests instead of by
 * opening it in Excel.
 */

/** Semicolon, because Excel on a German locale splits on it and would otherwise ignore commas. */
const DELIMITER = ";";

export const PARTICIPATION_COLUMNS = [
    "Employee",
    "Email",
    "Department",
    "Employee levels",
    "Course",
    "Course levels",
    "Course credits",
    "Trainer",
    "Course room",
    "Session",
    "Status",
    "Attended",
    "Credits awarded",
    "Decided by",
    "Decided at",
    "Attendance confirmed by",
    "Attendance confirmed at",
] as const;

/**
 * Excel reads a bare `=`, `+`, `-` or `@` at the start of a cell as a formula, and a course or a
 * trainer is free text somebody at the hotel typed. Prefixing an apostrophe keeps it text.
 */
const neutralised = (value: string) => /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;

/** ISO, so the column sorts and filters as a date rather than as text in any locale. */
const isoDate = (value?: Date | string): string => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

/**
 * The header row and the two words in the attended column, so the file arrives in the language the
 * person reading it works in. Forsthofgut's HR team works in German; the defaults here are what the
 * keys are, and the screen passes the translated set.
 */
export interface ParticipationCsvLabels {
    columns: readonly string[];
    yes: string;
    no: string;
}

const DEFAULT_LABELS: ParticipationCsvLabels = {
    columns: PARTICIPATION_COLUMNS,
    yes: "yes",
    no: "no",
};

const cell = (value: string | number | Date | null | undefined): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") return String(value);
    // The API client revives anything date-shaped, so a field typed as a string here can still
    // arrive as a Date. Left to `String()` it would land in the file as "Sat Sep 26 2026 13:47:18
    // GMT+0200 (Central European Summer Time)", which no spreadsheet reads as a date.
    if (value instanceof Date) return isoDate(value);

    const text = neutralised(value);
    return /["\n\r;,]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const line = (values: (string | number | Date | null | undefined)[]) =>
    values.map(cell).join(DELIMITER);

export const participationCsv = (
    rows: AcademyParticipationRowDTO[],
    labels: ParticipationCsvLabels = DEFAULT_LABELS,
): string => [
    line([...labels.columns]),
    ...rows.map(row => line([
        row.employeeName,
        row.employeeEmail,
        row.department,
        row.employeeLevels?.join(" "),
        row.courseName,
        row.courseLevels?.join(" "),
        row.courseCredits,
        row.trainer,
        row.room,
        isoDate(row.start),
        row.status,
        row.attended ? labels.yes : labels.no,
        row.creditsAwarded,
        row.decidedBy,
        isoDate(row.decidedAt),
        row.confirmedBy,
        isoDate(row.confirmedAt),
    ])),
].join("\r\n");

/** `akademie-teilnahme-2026-09-01-2026-09-30.csv`: sorts by range in a folder of exports. */
export const participationFileName = (start: Date, end: Date): string => {
    const day = (date: Date) => isoDate(date).slice(0, 10);
    return `akademie-teilnahme-${day(start)}-${day(end)}.csv`;
};
